import { Component, OnDestroy, OnInit } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { EstatusJSON } from 'src/app/features/shared/models/EstatusJSON';
import { CategoryFiltersDTO } from 'src/app/features/shared/models/CategoryFilters/CategoryFilters';
import { NotificationsToastService } from 'src/app/features/shared/services/Notifications-Toast.service';
import { SwalNotificationService } from 'src/app/features/shared/services/SwalNotification.service';
import { SessionService } from 'src/app/features/shared/services/Session.service';
import { ReportsService } from 'src/app/features/shared/services/ServiceCatalogEncapsulators/Reports.service';
import { ExcelUtilities, ReportExcelCellValue } from 'src/app/features/shared/helpers/utilities/ExcelUtilities';
import { EnumAlarmCategory, EnumAlarmCategoriesColors, EnumAlarmCategoriesIcons, EnumAlarmCategoriesValues } from 'src/app/features/shared/helpers/enums/EnumAlarmCategories';
import { MapHelperService } from 'src/services/map-helper.service';
import { LocationsService } from 'src/app/features/map-features/sections/locations-section/services/Locations.service';
import { LocationDTO } from 'src/app/features/map-features/sections/locations-section/models/LocationDTO';
import { LocationCategoryDTO } from 'src/app/features/map-features/sections/locations-section/models/LocationCategoryDTO';
import { StatusFlag } from 'src/app/features/map-features/sections/locations-section/models/StatusFlag';
import { ReportTabExcelDTO } from 'src/app/features/map-features/sections/locations-section/models/ReportTabExcelDTO';
import { FilterDialogData, FilterDialogResult, SelectedFilter } from 'src/app/features/map-features/sections/locations-section/models/Filters';
import { FilterCategoriesComponent } from 'src/app/features/map-features/sections/locations-section/components/2-panel-locations/organisms/filters/filters-categories/filter-categories.component';
import { ProspectsMapSectionService } from '../../../services/prospects-map-section.service';

interface ProspectCategoryTab {
  category: LocationCategoryDTO;
  total: number;
  filtersSelected: CategoryFiltersDTO[];
}

@Component({
  selector: 'prospects-panel',
  templateUrl: './prospects-panel.component.html',
  styleUrls: [
    '../../../../locations-section/components/2-panel-locations/organisms/panel-locations/panel-locations.component.css',
    '../../../../../ui/organisms/map-panel-button-toolbar/components/location-button-toolbar/location-button-toolbar.component.css',
    './prospects-panel.component.css'
  ],
  animations: [
    trigger('mostrarHerramientas', [
      state('visible', style({ opacity: 1, height: '*', minHeight: '*' })),
      state('hidden', style({ opacity: 0, height: '0px', minHeight: '0', overflow: 'hidden', display: 'none' })),
      transition('visible <=> hidden', animate('200ms ease-in-out')),
    ]),
  ],
})
export class ProspectsPanelComponent implements OnInit, OnDestroy {

  categoriesTabs: ProspectCategoryTab[] = [];
  selectedTabIndex: number = 0;
  selectedCategoryTab: ProspectCategoryTab = null;

  $Items: LocationDTO[] = [];
  isLocationListEnd: boolean = false;
  isLoading: boolean = false;

  isToolButtonPressed: boolean = false;

  filtersObject: FilterDialogData = { moduleName: null, filters: [], selected: [], idLocationCategory: null };
  ApplyFilter: boolean = false;
  excelDownloadOperation: string = 'Category';

  filterDialog: any;

  private currentFilters: CategoryFiltersDTO[] = [];
  private activeDetailsDialog: MatDialogRef<any> = null;

  subscription_GetList: Subscription;
  subscription_activeFilters: Subscription;
  subscription_AfterClosed: Subscription;
  subscription_postLocationsToExcel: Subscription;
  subscription_prospectDataChanged: Subscription;

  constructor(
    private dialog: MatDialog,
    private notificationsToastService: NotificationsToastService,
    private notifyService: SwalNotificationService,
    private sessionService: SessionService,
    private reportsService: ReportsService,
    private mapHelperService: MapHelperService,
    private locationsService: LocationsService,
    private prospectsMapSectionService: ProspectsMapSectionService
  ) { }

  ngOnInit(): void {
    this.loadCategories();

    this.subscription_activeFilters = this.prospectsMapSectionService.activeFilters$.subscribe(filters => {
      if (!filters) {
        this.currentFilters = [];
        this.$Items = [];
        return;
      }
      this.currentFilters = filters;
      this.ApplyFilter = this.selectedCategoryTab?.filtersSelected.length > 0;
      this.isLocationListEnd = false;
      this.getLocations(1);
    });

    //recarga el tab actual (lista) para reflejar el cambio
    this.subscription_prospectDataChanged = this.prospectsMapSectionService.getProspectDataChanged().subscribe(() => {
      this.isLocationListEnd = false;
      this.getLocations(1);
    });
  }

  ngOnDestroy(): void {
    this.subscription_GetList?.unsubscribe();
    this.subscription_activeFilters?.unsubscribe();
    this.subscription_AfterClosed?.unsubscribe();
    this.subscription_postLocationsToExcel?.unsubscribe();
    this.subscription_prospectDataChanged?.unsubscribe();
    if (this.filterDialog) {
      this.filterDialog.close();
    }
    if (this.activeDetailsDialog) {
      this.activeDetailsDialog.close();
      this.activeDetailsDialog = null;
    }
  }

  private loadCategories(): void {
    this.categoriesTabs = this.prospectsMapSectionService.getProspectCategories().map(category => ({
      category,
      total: category.total ?? 0,
      filtersSelected: [],
    }));
    this.selectTab({ index: 0 });
  }

  selectTab(event: any): void {
    const index = event?.index ?? 0;
    this.selectedTabIndex = index;
    this.selectedCategoryTab = this.categoriesTabs[index] || null;

    this.subscription_GetList?.unsubscribe();
    this.$Items = [];
    this.isLocationListEnd = false;

    if (!this.selectedCategoryTab) {
      return;
    }

    this.ApplyFilter = this.selectedCategoryTab.filtersSelected.length > 0;
    this.filtersObject = {
      moduleName: this.selectedCategoryTab.category.filterModule || null,
      filters: [],
      selected: this.mapToSelectedFilters(this.selectedCategoryTab.filtersSelected),
      idLocationCategory: this.selectedCategoryTab.category.id,
    };

    const isSameCategoryAndFilters = this.prospectsMapSectionService.getActiveCategory()?.id === this.selectedCategoryTab.category.id
      && JSON.stringify(this.prospectsMapSectionService.getCategoryFilters()) === JSON.stringify(this.selectedCategoryTab.filtersSelected);

    if (isSameCategoryAndFilters) {
      return;
    }

    this.prospectsMapSectionService.setActiveCategory(this.selectedCategoryTab.category);
    this.prospectsMapSectionService.setCategoryFilters(this.selectedCategoryTab.filtersSelected);
  }

  private mapToSelectedFilters(filters: CategoryFiltersDTO[]): SelectedFilter[] {
    return (filters || []).map(f => ({ key: f.Key, value: f.Value, module: f.Module || '' }));
  }

  getLocations(page: number = 1): void {
    if (!this.selectedCategoryTab || this.currentFilters.length === 0) {
      return;
    }

    const categoryTab = this.selectedCategoryTab;
    this.isLoading = true;

    this.subscription_GetList?.unsubscribe();
    this.subscription_GetList = this.locationsService.GetListByIdLocationCategoryPage(categoryTab.category.id, page, this.currentFilters).subscribe({
      next: (data: EstatusJSON) => {
        if (data.isSuccess) {
          categoryTab.total = data.result.contador;
          this.setLocationList(data, page);
        } else {
          categoryTab.total = 0;
          this.isLoading = false;
          Sentry.captureMessage('ProspectsPanel.getLocations: respuesta sin éxito', { level: 'warning', extra: { response: data } });
        }
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  setLocationList(data: any, page: number): void {
    const locationList = (data.result.listaLocaciones || []) as LocationDTO[];

    if (locationList.length == 0) {
      this.isLocationListEnd = true;
      this.setLocationPage([], page);
      return;
    }

    if (locationList.length < 20) {
      this.isLocationListEnd = true;
      this.setLocationPage(locationList, page);
      return;
    }

    this.setLocationPage(locationList, page);
  }

  setLocationPage(result: LocationDTO[], page: number): void {
    result.forEach(item => {
      item.panelIcon = item.icon;
      item.statusFlags = item.statusFlags || [];
      item.statusFlags.push(...this.getStatusFlagsByFlagTypes(item.flagTypes));
    });

    this.$Items = page == 1 ? result : this.$Items.concat(result);
    this.isLoading = false;
  }

  onScroll(event: any): void {
    if (this.isLocationListEnd || this.isLoading) {
      return;
    }
    const element = event.target;
    if (element.scrollHeight - element.scrollTop <= (element.clientHeight + 10)) {
      this.loadMoreLocations();
    }
  }

  loadMoreLocations(): void {
    const length = this.$Items.length;
    const residue = length % 20;
    const nextPage = residue != 0 ? Math.max(1, Math.floor(length / 20)) : (length / 20) + 1;
    this.getLocations(nextPage);
  }

  reloadProspectos(): void {
    this.prospectsMapSectionService.reload();
  }

  openFilters(): void {
    if (!this.selectedCategoryTab) {
      return;
    }
    const categoryTab = this.selectedCategoryTab;

    this.filterDialog = this.dialog.open(FilterCategoriesComponent, {
      data: {
        moduleName: categoryTab.category.filterModule || null,
        filters: [],
        idLocationCategory: categoryTab.category.id,
        selected: this.mapToSelectedFilters(categoryTab.filtersSelected),
      } as FilterDialogData,
      disableClose: false,
      panelClass: 'locations-filters-custom-popup',
      hasBackdrop: true,
      autoFocus: false,
      restoreFocus: false,
    });

    this.subscription_AfterClosed = this.filterDialog.afterClosed().subscribe((res: FilterDialogResult) => {
      if (!res || !res.search) {
        return;
      }

      const filtersSelected: SelectedFilter[] = res.filtersSelected?.length > 0 ? res.filtersSelected : [];
      const mappedFilters: CategoryFiltersDTO[] = filtersSelected.map(f => ({ Key: f.key, Value: f.value, Module: f.module || '' }));
      categoryTab.filtersSelected = mappedFilters;
      this.ApplyFilter = mappedFilters.length > 0;
      this.filtersObject = { ...this.filtersObject, selected: filtersSelected };
      this.prospectsMapSectionService.setCategoryFilters(mappedFilters);
    });
  }

  onCardClicked(item: LocationDTO): void {
    const coords = this.prospectsMapSectionService.getCoordsByIdentifier(item.identifier);
    const lat = coords ? coords.lat : Number(item.cordsY);
    const lng = coords ? coords.lng : Number(item.cordsX);

    this.activeDetailsDialog = this.prospectsMapSectionService.openProspectDetailsPopup({
      identifier: item.identifier,
      parentCoordX: item.cordsX,
      parentCoordY: item.cordsY,
      FlyToOnOpen: true,
      UpdateActiveLayer: true,
    });
    this.activeDetailsDialog.afterClosed().subscribe(() => this.activeDetailsDialog = null);

    if (!isNaN(lat) && !isNaN(lng)) {
      this.mapHelperService.FlyToCameraFast(lat.toString(), lng.toString(), '15', 0);
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
    this.notificationsToastService.showSuccess('Texto copiado al portapapeles', text);
  }

  getTabExcel(): void {
    if (!this.selectedCategoryTab) {
      return;
    }
    const title = `Descargar excel de tipo ${this.selectedCategoryTab.category?.shortName || 'Prospectos'}`;
    this.notifyService.customConfirmMessageInPanel(title, '¿Seguro que quieres descargar la lista en excel?').then((result) => {
      if (!result.value) {
        return;
      }
      this.ShowDownloadingMessage();
      const filtersForExport: SelectedFilter[] = this.currentFilters.map(f => ({ key: f.Key, value: f.Value, module: f.Module || '' }));
      this.subscription_postLocationsToExcel = this.reportsService.postLocationsTabToExcel(this.excelDownloadOperation, this.selectedCategoryTab.category.id, '', filtersForExport)
        .subscribe({
          next: (data: EstatusJSON) => {
            if (data.isSuccess) {
              this.exportLocationsTabReportToExcel(data.result as ReportTabExcelDTO[]);
            } else {
              Sentry.captureMessage('ProspectsPanel.getTabExcel: descarga sin datos', { level: 'warning', extra: { response: data } });
              this.notifyService.MessageError('Archivo no descargado, No hay datos');
            }
          },
          error: (error) => {
            console.error('Error descargando excel de prospectos', error);
            this.notifyService.MessageError('Error al descargar el archivo');
          }
        });
    });
  }

  private exportLocationsTabReportToExcel(rows: ReportTabExcelDTO[]): void {
    const header = ['Identificador', 'Descripción', 'Cordenadas', 'Google Maps', 'Ultima Actualizacion'];
    const data = rows.map(row => [
      row.identifier,
      row.description ? row.description : 'N/A',
      `${ExcelUtilities.max6DecTruncate(row.cordsY)}, ${ExcelUtilities.max6DecTruncate(row.cordsX)}`,
      ExcelUtilities.getGoogleMapsURL(row.cordsY, row.cordsX),
      ExcelUtilities.parseDate(row.fechaUltimaActualizacion)
    ]) as ReportExcelCellValue[][];
    const title = `Reporte de Prospectos - ${new Date().toISOString().slice(0, 10)}`;
    const cliente = this.sessionService.Customer?.description || 'Cliente Desconocido';

    ExcelUtilities.exportReportExcel(title, header, data, cliente);
  }

  private ShowDownloadingMessage(): void {
    this.notifyService.MessageDownloading().then(d => {
      if (d.isConfirmed) {
        this.notificationsToastService.showInfoExtended('', 'Procesando solicitud de descarga...');
      }
    });
  }

  getStatusFlagsByFlagTypes(flagTypes: number[]): StatusFlag[] {
    const statusFlags: StatusFlag[] = [];
    if (flagTypes == null || flagTypes.length < 1) {
      return statusFlags;
    }
    flagTypes.forEach(flagType => {
      const statusKey = Object.keys(EnumAlarmCategory).find(key => EnumAlarmCategory[key] === flagType);
      if (statusKey) {
        statusFlags.push({
          flagName: EnumAlarmCategoriesValues[statusKey],
          priorityHex: EnumAlarmCategoriesColors[statusKey],
          flagIcon: EnumAlarmCategoriesIcons[statusKey],
        } as StatusFlag);
      }
    });
    return statusFlags;
  }
}
